import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Loader2, ShieldAlert, Tag, Check, X } from 'lucide-react';

const CURRENCY_SYMBOLS = {
  'NGN': '₦', 'GHS': 'GH₵', 'KES': 'KSh', 'ZAR': 'R',
  'CAD': 'CA$', 'USD': '$', 'GBP': '£', 'EUR': '€'
};

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

// Calculate total price including platform fee (5.5%) when passed to buyers
// Service fee (13.5%) is ALWAYS deducted from organizer's payout, never passed to buyers
const calculateTotalWithFees = (basePrice, quantity, serviceFeeEnabled = false, absorbFees = true) => {
  const subtotal = basePrice * quantity;
  if (absorbFees) return subtotal;
  const platformFee = 0.055;
  return Math.ceil(subtotal * (1 + platformFee));
};

export function TicketPurchaseModal({
  open,
  onOpenChange,
  event,
  selectedTicket,
  setSelectedTicket,
  quantity,
  setQuantity,
  isAuthenticated,
  user,
  paymentGateway,
  isFreeEvent,
  isReservationEvent,
  hasReservationLimit,
  totalAvailable
}) {
  const navigate = useNavigate();
  const cs = CURRENCY_SYMBOLS[event?.currency] || '₦';
  const [purchasing, setPurchasing] = useState(false);
  // iOS/mobile: render a native <select> instead of the Radix dropdown to avoid the
  // touch-scroll freeze caused by the portal/scroll-lock overlay with many ticket types
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  const [paymentEmail, setPaymentEmail] = useState('');
  const [guestName, setGuestName] = useState('');
  // Vendor name — rendered only when event.collect_vendor_name===true
  const [vendorName, setVendorName] = useState('');
  const [emailError, setEmailError] = useState('');
  const [userTicketCount, setUserTicketCount] = useState(0);
  const maxPerUser = event?.max_tickets_per_user || null;
  
  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');

  // Fetch user's existing ticket count for per-user limit enforcement
  useEffect(() => {
    if (!open || !maxPerUser || !event?.event_id) return;
    const fetchUserTicketCount = async () => {
      try {
        if (isAuthenticated && user) {
          const res = await axios.get(`${API_URL}/api/tickets/my-tickets`);
          const eventTickets = (res.data || []).filter(t => t.event_id === event.event_id && t.status === 'confirmed');
          setUserTicketCount(eventTickets.length);
        }
      } catch {
        setUserTicketCount(0);
      }
    };
    fetchUserTicketCount();
  }, [open, maxPerUser, event?.event_id, isAuthenticated, user]);

  const validateEmail = (email) => {
    if (!email) return 'Email address is required';
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!re.test(email)) return 'Please enter a valid email address (e.g. name@example.com)';
    return '';
  };

  const handleEmailChange = (e) => {
    const val = e.target.value;
    setPaymentEmail(val);
    if (emailError && val) {
      setEmailError(validateEmail(val));
    }
  };

  const handleEmailBlur = () => {
    if (paymentEmail) {
      setEmailError(validateEmail(paymentEmail));
    }
  };

  // Reset coupon when ticket or quantity changes
  useEffect(() => {
    setAppliedCoupon(null);
    setCouponError('');
  }, [selectedTicket, quantity]);

  const handleApplyCoupon = async () => {
    const trimmedCode = couponCode.trim();
    if (!trimmedCode) return;
    if (!event?.event_id) {
      setCouponError('Event not loaded yet. Please try again.');
      return;
    }
    setCouponLoading(true);
    setCouponError('');
    try {
      const res = await axios.post(`${API_URL}/api/coupons/validate`, {
        event_id: event.event_id,
        code: trimmedCode,
        ticket_price: selectedTicket?.price || 0,
        quantity: parseInt(quantity)
      });
      setAppliedCoupon(res.data);
      toast.success(res.data.message || 'Coupon applied!');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to validate coupon';
      setCouponError(msg);
      setAppliedCoupon(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError('');
  };

  // Calculate final total considering coupon
  const getSubtotal = () => {
    if (!selectedTicket) return 0;
    return selectedTicket.price * parseInt(quantity);
  };

  const getDiscountedSubtotal = () => {
    const subtotal = getSubtotal();
    if (!appliedCoupon) return subtotal;
    return Math.max(subtotal - appliedCoupon.discount_amount, 0);
  };

  const getFinalTotal = () => {
    const discountedSubtotal = getDiscountedSubtotal();
    if (discountedSubtotal === 0) return 0;
    if (event?.absorb_fees) return discountedSubtotal;
    return Math.ceil(discountedSubtotal * 1.055);
  };

  // Auto-select first available ticket when modal opens
  useEffect(() => {
    if (open && !selectedTicket && event?.ticket_types?.length > 0) {
      const firstAvailable = event.ticket_types.find(t => t.available > 0);
      if (firstAvailable) {
        setSelectedTicket(firstAvailable);
      }
    }
    // Pre-fill email for authenticated users
    if (open && isAuthenticated && user?.email && !paymentEmail) {
      setPaymentEmail(user.email);
    }
  }, [open, selectedTicket, event, setSelectedTicket, isAuthenticated, user]);

  const handlePurchase = async () => {
    if (!selectedTicket || !isAuthenticated) return;

    if (!selectedTicket.is_free) {
      const emailToUse = paymentEmail || user?.email;
      const emailErr = validateEmail(emailToUse || '');
      if (emailErr) {
        setEmailError(emailErr);
        toast.error('Please enter a valid email address for ticket delivery');
        return;
      }
    }

    setPurchasing(true);
    try {
      if (selectedTicket.is_free) {
        await axios.post(
          `${API_URL}/api/tickets/rsvp`,
          {
            event_id: event.event_id,
            ticket_type_id: selectedTicket.ticket_id,
            quantity: parseInt(quantity)
          },
          
        );
        toast.success('RSVP successful! Check your tickets.');
        onOpenChange(false);
        navigate('/my-tickets');
      } else if (getFinalTotal() === 0) {
        // 100% coupon => zero total. Skip Paystack entirely and issue the ticket via the
        // free-coupon checkout endpoint (server re-validates that the coupon zeroes the total).
        await axios.post(`${API_URL}/api/tickets/checkout/free`, {
          event_id: event.event_id,
          ticket_type_id: selectedTicket.ticket_id,
          quantity: parseInt(quantity),
          coupon_id: appliedCoupon?.coupon_id
        });
        toast.success('Ticket issued! Check your tickets.');
        onOpenChange(false);
        navigate('/my-tickets');
      } else {
        const emailToUse = paymentEmail || user?.email;
        
        if (paymentGateway.gateway === 'stripe') {
          const params = {
            event_id: event.event_id,
            ticket_type_id: selectedTicket.ticket_id,
            quantity: parseInt(quantity),
            email: emailToUse
          };
          if (appliedCoupon) params.coupon_id = appliedCoupon.coupon_id;
          
          const response = await axios.post(
            `${API_URL}/api/stripe/checkout/create`,
            null,
            { params,  }
          );
          
          if (response.data.checkout_url) {
            window.location.href = response.data.checkout_url;
          } else {
            toast.error('Payment initialization failed. Please try again.');
          }
        } else {
          const response = await axios.post(
            `${API_URL}/api/tickets/payment/initialize`,
            {
              email: emailToUse,
              event_id: event.event_id,
              tickets: [{ ticket_type_id: selectedTicket.ticket_id, quantity: parseInt(quantity) }],
              total_amount: getFinalTotal(),
              coupon_id: appliedCoupon?.coupon_id || null
            },
            
          );
          
          if (response.data.authorization_url) {
            window.location.href = response.data.authorization_url;
          } else {
            toast.error('Payment initialization failed. Please try again.');
          }
        }
      }
    } catch (error) {
      console.error('Purchase error:', error);
      const errMsg = error.response?.data?.detail || 'Failed to process. Please try again.';
      toast.error(errMsg);
      if (errMsg.includes('Coupon no longer valid')) {
        setAppliedCoupon(null);
        setCouponCode('');
      }
    } finally {
      setPurchasing(false);
    }
  };

  const handleGuestCheckout = async () => {
    const emailErr = validateEmail(paymentEmail);
    if (emailErr) {
      setEmailError(emailErr);
      toast.error(emailErr);
      return;
    }
    
    if (!guestName || guestName.trim().length < 2) {
      toast.error('Please enter your name');
      return;
    }
    // Vendor name required when the organizer has opted-in for this event.
    if (event?.collect_vendor_name === true && !vendorName.trim()) {
      toast.error('Please enter the vendor / store name');
      return;
    }
    
    setPurchasing(true);
    try {
      if (paymentGateway.gateway === 'stripe') {
        const params = {
          event_id: event.event_id,
          ticket_type_id: selectedTicket.ticket_id,
          quantity: parseInt(quantity),
          email: paymentEmail,
          guest_name: guestName.trim()
        };
        if (appliedCoupon) params.coupon_id = appliedCoupon.coupon_id;
        if (event?.collect_vendor_name === true) params.vendor_name = vendorName.trim();
        
        const response = await axios.post(`${API_URL}/api/stripe/checkout/create`, null, { params });
        
        if (response.data.checkout_url) {
          window.location.href = response.data.checkout_url;
        } else {
          toast.error('Payment initialization failed. Please try again.');
        }
      } else {
        // Use Paystack guest checkout endpoint (no auth required)
        const response = await axios.post(`${API_URL}/api/tickets/guest/payment/initialize`, {
          event_id: event.event_id,
          tickets: [{ ticket_type_id: selectedTicket.ticket_id, quantity: parseInt(quantity) }],
          total_amount: getFinalTotal(),
          coupon_id: appliedCoupon?.coupon_id || null,
          guest_info: {
            email: paymentEmail,
            name: guestName.trim()
          },
          ...(event?.collect_vendor_name === true
            ? { vendor_name: vendorName.trim() }
            : {})
        });
        
        if (response.data.authorization_url) {
          window.location.href = response.data.authorization_url;
        } else {
          toast.error('Payment initialization failed. Please try again.');
        }
      }
    } catch (error) {
      console.error('Guest checkout error:', error);
      const errMsg = error.response?.data?.detail || 'Payment failed. Please try again.';
      toast.error(errMsg);
      if (errMsg.includes('Coupon no longer valid')) {
        setAppliedCoupon(null);
        setCouponCode('');
      }
    } finally {
      setPurchasing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="ticket-modal">
        <DialogHeader>
          <DialogTitle>
            {isReservationEvent ? 'Reserve Your Spot' : 'Select Tickets'}
          </DialogTitle>
          <DialogDescription>
            Choose your ticket type and quantity for {event?.title}
          </DialogDescription>
        </DialogHeader>

        {/* Non-Refundable Warning */}
        {event?.is_non_refundable && (
          <div className="flex items-start gap-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <ShieldAlert className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-600 dark:text-red-400">
                Non-Refundable Event
              </p>
              <p className="text-xs text-red-500/80 mt-0.5">
                Tickets for this event cannot be refunded after purchase. Please make sure you can attend before buying.
              </p>
            </div>
          </div>
        )}

        <div className="space-y-4 py-4">
          {/* Ticket Type Selection */}
          {!isReservationEvent && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Ticket Type</label>
              {isMobile ? (
                <select
                  data-testid="modal-ticket-select"
                  className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                  value={selectedTicket?.ticket_id || ''}
                  onChange={(e) => {
                    const ticket = event?.ticket_types.find(t => t.ticket_id === e.target.value);
                    setSelectedTicket(ticket);
                  }}
                >
                  <option value="" disabled>Select ticket type</option>
                  {event?.ticket_types.map((ticket) => (
                    <option
                      key={ticket.ticket_id}
                      value={ticket.ticket_id}
                      disabled={ticket.available === 0}
                    >
                      {ticket.name} - {isFreeEvent || ticket.is_free ? 'Free' : `${cs}${ticket.price.toLocaleString()}`}
                    </option>
                  ))}
                </select>
              ) : (
                <Select
                  value={selectedTicket?.ticket_id}
                  onValueChange={(value) => {
                    const ticket = event?.ticket_types.find(t => t.ticket_id === value);
                    setSelectedTicket(ticket);
                  }}
                >
                  <SelectTrigger data-testid="modal-ticket-select">
                    <SelectValue placeholder="Select ticket type" />
                  </SelectTrigger>
                  <SelectContent>
                    {event?.ticket_types.map((ticket) => (
                      <SelectItem 
                        key={ticket.ticket_id} 
                        value={ticket.ticket_id}
                        disabled={ticket.available === 0}
                      >
                        {ticket.name} - {isFreeEvent || ticket.is_free ? 'Free' : `${cs}${ticket.price.toLocaleString()}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {/* Quantity Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium">{isReservationEvent ? 'Number of Spots' : 'Quantity'}</label>
            {(isFreeEvent || selectedTicket?.is_free) && !maxPerUser ? (
              <div className="flex items-center h-10 px-3 rounded-md border bg-muted text-muted-foreground text-sm" data-testid="modal-quantity-locked">
                1 ticket (free event limit)
              </div>
            ) : (
              <Select value={quantity} onValueChange={setQuantity}>
                <SelectTrigger data-testid="modal-quantity-select">
                  <SelectValue placeholder="Select quantity" />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 10].map((num) => {
                    const stockAvailable = selectedTicket?.available;
                    const availableDisabled = hasReservationLimit
                      ? totalAvailable < num
                      : (stockAvailable !== null && stockAvailable !== undefined && stockAvailable < num);
                    const effectiveMax = maxPerUser || (stockAvailable !== null && stockAvailable !== undefined ? stockAvailable : 999);
                    const perUserDisabled = maxPerUser ? (userTicketCount + num > maxPerUser) : false;
                    const overStock = num > effectiveMax;
                    return (
                      <SelectItem 
                        key={num} 
                        value={num.toString()}
                        disabled={availableDisabled || perUserDisabled || overStock}
                      >
                        {num} {isReservationEvent ? 'spot' : 'ticket'}{num > 1 ? 's' : ''}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            )}
            {maxPerUser && (
              <p className="text-xs text-muted-foreground" data-testid="per-user-limit-info">
                {userTicketCount > 0
                  ? `You've claimed ${userTicketCount} of ${maxPerUser} allowed tickets. ${Math.max(0, maxPerUser - userTicketCount)} remaining.`
                  : `Limit: ${maxPerUser} ticket${maxPerUser > 1 ? 's' : ''} per person`
                }
              </p>
            )}
            {maxPerUser && userTicketCount >= maxPerUser && (
              <p className="text-sm text-destructive font-medium" data-testid="per-user-limit-error">
                You have reached the maximum ticket limit for this event
              </p>
            )}
          </div>

          {/* Coupon Code Input */}
          {selectedTicket && !isFreeEvent && !selectedTicket.is_free && !isReservationEvent && (
            <div className="space-y-2" data-testid="coupon-section">
              <Label className="text-sm">Coupon Code</Label>
              {appliedCoupon ? (
                <div className="flex items-center justify-between p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                  <div className="flex items-center gap-2">
                    <Tag className="w-4 h-4 text-emerald-500" />
                    <span className="font-mono font-semibold text-sm">{appliedCoupon.code}</span>
                    <span className="text-xs text-emerald-500">
                      -{appliedCoupon.discount_type === 'percentage' ? `${appliedCoupon.discount_value}%` : `${cs}${appliedCoupon.discount_amount.toLocaleString()}`}
                    </span>
                  </div>
                  <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={removeCoupon} data-testid="remove-coupon-btn">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    value={couponCode}
                    onChange={e => { setCouponCode(e.target.value.toUpperCase()); setCouponError(''); }}
                    placeholder="Enter coupon code"
                    className="font-mono uppercase flex-1"
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleApplyCoupon())}
                    data-testid="coupon-code-checkout-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleApplyCoupon}
                    disabled={!couponCode.trim() || couponLoading}
                    data-testid="apply-coupon-btn"
                  >
                    {couponLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                  </Button>
                </div>
              )}
              {couponError && <p className="text-xs text-red-500" data-testid="coupon-error">{couponError}</p>}
            </div>
          )}

          {/* Total */}
          {selectedTicket && !isReservationEvent && (
            <div className="p-4 rounded-xl bg-muted">
              {!isFreeEvent && !selectedTicket.is_free && (
                <div className="space-y-1 mb-2 pb-2 border-b border-border">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="font-mono">{cs}{getSubtotal().toLocaleString()}</span>
                  </div>
                  {appliedCoupon && (
                    <div className="flex justify-between text-sm text-emerald-500">
                      <span>Discount ({appliedCoupon.code})</span>
                      <span className="font-mono">-{cs}{appliedCoupon.discount_amount.toLocaleString()}</span>
                    </div>
                  )}
                  {!event?.absorb_fees && getDiscountedSubtotal() > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Platform fee (5.5%)</span>
                      <span className="font-mono">{cs}{Math.ceil(getDiscountedSubtotal() * 0.055).toLocaleString()}</span>
                    </div>
                  )}
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Total</span>
                <span className="text-2xl font-bold font-mono">
                  {isFreeEvent || selectedTicket.is_free || getFinalTotal() === 0 ? (
                    <span className="text-secondary">Free</span>
                  ) : (
                    `${cs}${getFinalTotal().toLocaleString()}`
                  )}
                </span>
              </div>
            </div>
          )}

          {/* Payment Gateway Indicator */}
          {selectedTicket && !selectedTicket.is_free && !isFreeEvent && (
            <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground py-2">
              <span>Powered by</span>
              {paymentGateway.gateway === 'stripe' ? (
                <span className="font-semibold text-[#635bff]">Stripe</span>
              ) : (
                <span className="font-semibold text-[#00c3f7]">Paystack</span>
              )}
              <span className="text-muted-foreground/50">•</span>
              <span className="capitalize">{paymentGateway.region}</span>
            </div>
          )}

          {/* Email for Authenticated Users */}
          {isAuthenticated && selectedTicket && !isFreeEvent && !selectedTicket.is_free && (
            <div className="space-y-2">
              <Label htmlFor="auth-email">Email Address *</Label>
              <Input
                id="auth-email"
                type="email"
                value={paymentEmail || user?.email || ''}
                onChange={handleEmailChange}
                onBlur={handleEmailBlur}
                placeholder="Enter email for ticket delivery & receipt"
                required
                className={emailError ? 'border-red-500 focus-visible:ring-red-500' : ''}
                data-testid="auth-email-input"
              />
              {emailError ? (
                <p className="text-xs text-red-500" data-testid="auth-email-error">{emailError}</p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Your ticket and payment receipt will be sent to this email
                </p>
              )}
            </div>
          )}

          {/* Guest Checkout for Non-Authenticated Users */}
          {!isAuthenticated && selectedTicket && (
            <div className="space-y-4">
              {(selectedTicket.is_free || isFreeEvent || getFinalTotal() === 0) ? (
                <div className="space-y-3">
                  <div className="p-4 rounded-xl bg-muted/50 border border-border text-center">
                    <p className="text-sm text-muted-foreground mb-3">
                      Sign in to get your free ticket
                    </p>
                    <Button 
                      className="w-full"
                      onClick={() => navigate('/login', { state: { from: `/events/${event.event_id}` } })}
                      data-testid="sign-in-for-free-ticket-btn"
                    >
                      Sign In to Continue
                    </Button>
                  </div>
                  <p className="text-xs text-center text-muted-foreground">
                    Don't have an account?{' '}
                    <span 
                      className="text-primary cursor-pointer hover:underline"
                      onClick={() => navigate('/register', { state: { from: `/events/${event.event_id}` } })}
                    >
                      Register here
                    </span>
                  </p>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="guest-name">Full Name *</Label>
                    <Input
                      id="guest-name"
                      type="text"
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                      placeholder="Enter your full name"
                      required
                      data-testid="guest-name-input"
                    />
                  </div>

                  {event?.collect_vendor_name === true && (
                    <div className="space-y-2" data-testid="checkout-vendor-name-block">
                      <Label htmlFor="modal-vendor-name">Vendor / Store Name *</Label>
                      <Input
                        id="modal-vendor-name"
                        type="text"
                        value={vendorName}
                        onChange={(e) => setVendorName(e.target.value)}
                        placeholder="e.g., Mama Put Kitchen"
                        maxLength={100}
                        required
                        data-testid="checkout-vendor-name-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        Required for this event. Not shown on your receipt.
                      </p>
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <Label htmlFor="guest-email">Email Address *</Label>
                    <Input
                      id="guest-email"
                      type="email"
                      value={paymentEmail}
                      onChange={handleEmailChange}
                      onBlur={handleEmailBlur}
                      placeholder="Enter your email address"
                      required
                      className={emailError ? 'border-red-500 focus-visible:ring-red-500' : ''}
                      data-testid="guest-email-input"
                    />
                    {emailError ? (
                      <p className="text-xs text-red-500" data-testid="guest-email-error">{emailError}</p>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        Your ticket and receipt will be sent to this email
                      </p>
                    )}
                  </div>
                  
                  <Button 
                    className="w-full h-12 text-lg gap-2"
                    onClick={handleGuestCheckout}
                    disabled={purchasing || !paymentEmail || !!validateEmail(paymentEmail) || !guestName || (event?.collect_vendor_name === true && !vendorName.trim())}
                    data-testid="guest-checkout-btn"
                  >
                    {purchasing ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Checkout as Guest'
                    )}
                  </Button>
                  
                  <p className="text-xs text-center text-muted-foreground">
                    Total: {cs}{calculateTotalWithFees(selectedTicket.price, parseInt(quantity), event?.service_fee_enabled, event?.absorb_fees).toLocaleString()}
                  </p>
                  
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-background px-2 text-muted-foreground">Or</span>
                    </div>
                  </div>
                  
                  <Button 
                    variant="outline"
                    className="w-full"
                    onClick={() => navigate('/login', { state: { from: `/events/${event.event_id}` } })}
                    data-testid="sign-in-to-purchase-btn"
                  >
                    Sign In to Purchase
                  </Button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer for Authenticated Users */}
        {isAuthenticated && (
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="modal-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handlePurchase}
              disabled={purchasing || !selectedTicket || (!isFreeEvent && !selectedTicket?.is_free && !!(validateEmail(paymentEmail || user?.email || '')))}
              className="gap-2"
              data-testid="modal-purchase-btn"
            >
              {purchasing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Processing...
                </>
              ) : (
                isReservationEvent ? 'Confirm Reservation' :
                isFreeEvent || selectedTicket?.is_free ? 'Get Free Ticket' :
                `Pay ${cs}${getFinalTotal().toLocaleString()}`
              )}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
